import React, { Dispatch, SetStateAction } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, EffectCoverflow } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { useNoAuthHooks } from '../../../../../../../Hooks/hooks';
import useProfileHook from './profile-hook';
import { generateContactLink } from '../../../../../../../Utilits/utilits';
import { ICompanyResponse } from '../../../../../../../Interfaces/responseTypes';
import { ContactLinkTypes } from '../../../../../../../Interfaces/componentTypes';
import { VisitorPageContents } from '../../../../visitorPage';
import ProfileHeader from '../../Components/Headers/ProfileHeader/profileHeader';
import useLanguagesHooks from '../../../../../../../Components/Languages/setLanguage-hooks';
import { CalendarIcon, ClockIcon, LocationIcon, PhoneIcon } from '../../../../../../../Components/IconComponents';
import styles from './profile.module.scss';

interface IByteProfileProps {
  data: ICompanyResponse,
  setContentType: Dispatch<SetStateAction<VisitorPageContents>>,
  is_user: boolean,
  onOpenReserveDialog: () => void
}

const MrByteProfile: React.FC<IByteProfileProps> = ({ data, setContentType, is_user, onOpenReserveDialog }) => {
  const { logo, name, id, business_profile, contacts, services } = data;
  const { t } = useLanguagesHooks();
  const { baseURL } = useNoAuthHooks();
  const { isOpen, freeDays, contactItems, hasOtherContacts } = useProfileHook(data);

  return (
    <div className={styles.page}>
      <ProfileHeader
        logo={logo}
        title={name}
        companyId={id}
        setContentType={setContentType}
        is_user={is_user}
        services={services}
        onOpenReserveDialog={onOpenReserveDialog}
      />
      <div className={styles.pageContent}>
        <div className={styles.isOpenBox}>
          <div className={isOpen ? styles.open : styles.close}>
            <p>{isOpen ? t('My_Business.Business_Profile.Page.Open') : t('My_Business.Business_Profile.Page.Close')}</p>
          </div>
        </div>
        <div className={styles.infoBox}>
          <div className={styles.infoItem}>
            <CalendarIcon color='var(--icon-color)' width={25} height={25} />
            <p>
              {
                business_profile?.full_week ? (
                  t('My_Business.Business_Profile.Page.Full_Week', 'We are working every day')
                ) : freeDays.length > 1 ? (
                  <>
                    {t('My_Business.Business_Profile.Page.Free_Days', 'Free days')}
                    <span style={{ color: 'var(--primary-hover)' }}>{freeDays.join(', ')}</span>
                  </>
                ) : (
                  <>
                    {t('My_Business.Business_Profile.Page.Free_Day', 'Free day')}
                    <span style={{ color: 'var(--primary-hover)' }}>{freeDays.join(', ')}</span>
                  </>
                )
              }
            </p>

          </div>
          <div className={styles.infoItem}>
            <ClockIcon width={25} height={25} color='var(--icon-color)'/>
            <p>
              {
                business_profile?.is_24
                  ?
                  <>
                    {t('My_Business.Business_Profile.Page.24')}
                  </>
                  :
                  <>
                    {business_profile?.start_time?.slice(0, 5)} - {business_profile?.end_time?.slice(0, 5)}
                  </>
              }
            </p>
          </div>
          <div className={styles.infoItem}>
            <LocationIcon width={25} height={25} color='var(--icon-color)'/>
            <p>
              {business_profile?.address}
            </p>
          </div>
          {
            !!contacts?.phone_numbers?.length && (
              <div className={styles.infoItem}>
                <PhoneIcon width={25} height={25} color='var(--icon-color)'/>
                <p>
                  {contacts.phone_numbers.map((num, index) => (
                    <a
                      key={index}
                      href={`tel:${num}`}
                      style={{ marginRight: 5, color: 'inherit', }}
                    >
                      {num}
                      {index !== contacts.phone_numbers?.length! - 1 && ','}&nbsp;
                    </a>
                  ))}
                </p>
              </div>
            )
          }
        </div>
        {
          business_profile?.gallery
          &&
          <div className={styles.sliderWrapper}>
            <Swiper
              modules={[Navigation, Autoplay, EffectCoverflow]}
              effect="coverflow"
              centeredSlides={true}
              slidesPerView={'auto'}
              spaceBetween={40}
              loop={true}
              autoplay={{ delay: 1500, disableOnInteraction: false }}
              navigation
              speed={800}
              coverflowEffect={{
                rotate: 30,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: false,
              }}
              className={styles.swiperContainer}
            >
              {business_profile?.gallery.map((img, index) => (
                <SwiperSlide key={index} className={styles.swiperSlide}>
                  <div className={styles.imageWrapper}>
                    <img
                      src={`${baseURL}${img}`}
                      alt={`Slide ${index}`}
                      className={styles.galleryImage}
                      onClick={() => console.log('Clicked image:', img)}
                    />
                    <div className={styles.overlay} />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        }
        {
          hasOtherContacts &&
          <div className={styles.contactsSection}>
            <div className={styles.contactsHeader}>
              <div />
              <p> {t('My_Business.Business_Profile.Page.Contact')}</p>
              <div />
            </div>
            <div className={styles.contactsBox}>
              {
                contactItems.length > 0
                &&
                contactItems.map((item) => {
                  const contactLink = generateContactLink(item?.id as ContactLinkTypes, item?.value! as string);
                  return (
                    <a
                      href={contactLink?.fallback}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.linkWrapper}
                    >
                      <img
                        src={item.icon}
                        alt="view.svg"
                      />
                    </a>
                  )
                })
              }
            </div>
          </div>
        }
      </div>
    </div>
  )
}

export default MrByteProfile
